/**
 * Tratamento de erros do vue-router.
 *
 * Contexto (card 86ak5gky5): o handler anterior transformava QUALQUER erro de
 * rota em `O sistema foi atualizado, atualizando a página...` + reload. A
 * intenção era boa — depois de um deploy, o chunk lazy que o navegador tenta
 * baixar não existe mais e a única saída é recarregar —, mas o catch-all
 * escondia erro de código atrás de uma mensagem de deploy e, quando o erro
 * persistia (ex.: SyntaxError num módulo), o app entrava em ciclo de reload
 * sem nunca mostrar a causa nem gerar sinal no monitoramento.
 *
 * Agora: só erro de carregamento de chunk recarrega — e no máximo UMA vez por
 * sessão de navegação; o resto é reportado e exibido como erro de verdade.
 */

/** Erros de navegação abortada/duplicada: ruído normal do vue-router. */
const IGNORED_NAMES = ['NavigationDuplicated', 'NavigationAborted']

/**
 * Falha ao baixar um módulo/chunk — sintoma clássico de deploy novo com o
 * bundle antigo em cache. Cobre Vite (fetch dynamically imported module),
 * webpack legado (Loading chunk X failed), Safari (Importing a module script
 * failed) e o caso em que o servidor devolve o index.html no lugar do .js.
 */
const CHUNK_ERROR_PATTERNS = [
  /loading chunk \S+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /is not a valid javascript mime type/i,
  /failed to load module script/i
]

export function isChunkLoadError(error) {
  if (!error) return false
  if (error.name === 'ChunkLoadError') return true

  // vue-router embrulha a falha do componente assíncrono; a causa original
  // pode vir em `cause`. Junta tudo num texto só para casar os padrões.
  const text = [error.name, error.message, error.cause?.message]
    .filter(Boolean)
    .join(' ')

  return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(text))
}

export function isIgnoredNavigationError(error) {
  return Boolean(error) && IGNORED_NAMES.includes(error.name)
}

/** Janela em que um segundo reload é considerado ciclo, não recuperação. */
export const RELOAD_GUARD_MS = 30000
export const RELOAD_GUARD_KEY = 'router:lastChunkReloadAt'

/**
 * @param {Error} error
 * @param {object} deps injetadas para manter a função testável sem router/DOM
 * @param {(msg: string) => void} deps.toastInfo
 * @param {(msg: string) => void} deps.toastError
 * @param {() => void} deps.reload
 * @param {(error: Error, context?: object) => void} [deps.reportError]
 * @param {Storage} [deps.storage] sessionStorage (ausente em SSR/testes)
 * @param {() => number} [deps.now]
 * @returns {'ignored'|'reloaded'|'reload-loop'|'reported'}
 */
export function handleRouterError(error, deps) {
  const {
    toastInfo,
    toastError,
    reload,
    reportError,
    storage,
    now = () => Date.now()
  } = deps

  if (isIgnoredNavigationError(error)) {
    return 'ignored'
  }

  if (isChunkLoadError(error)) {
    // Guarda anti-loop: se já recarregamos há pouco por esse motivo, o reload
    // não está resolvendo — para de recarregar e mostra o problema.
    const last = Number(readStorage(storage, RELOAD_GUARD_KEY) || 0)
    if (last && now() - last < RELOAD_GUARD_MS) {
      if (reportError) {
        reportError(error, { source: 'router.onError', reloadLoop: true })
      }
      toastError(
        'Não foi possível carregar esta tela. Atualize a página (Ctrl+F5) ou tente novamente em instantes.'
      )
      return 'reload-loop'
    }

    writeStorage(storage, RELOAD_GUARD_KEY, String(now()))
    toastInfo('O sistema foi atualizado, atualizando a página...')
    reload()
    return 'reloaded'
  }

  // Erro de código: reportar e mostrar como erro — nunca recarregar em cima,
  // que é o que apagava o rastro da causa.
  if (reportError) {
    reportError(error, { source: 'router.onError' })
  }
  toastError('Não foi possível abrir esta tela. Nossa equipe foi notificada.')
  return 'reported'
}

function readStorage(storage, key) {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeStorage(storage, key, value) {
  try {
    if (storage) {
      storage.setItem(key, value)
    }
  } catch {
    // modo anônimo/quota: sem guarda de loop, mas o reload segue valendo
  }
}
